import React from 'react';
import { Users, Briefcase, GraduationCap, DollarSign, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { PageTransition } from '../components/PageTransition';
export function Employees() {
  return <PageTransition data-id="element-280">
      <div className="bg-white" data-id="element-281">
        {/* Page Header */}
        <div className="bg-brand-light py-20 lg:py-32" data-id="element-282">
          <div className="container mx-auto px-4 md:px-6 text-center" data-id="element-283">
            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-brand-blue mb-6 tracking-tight" data-id="element-284">
              Careers at MediRide USA
            </motion.h1>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.1
          }} className="text-lg md:text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed" data-id="element-285">
              Join a team of dedicated professionals making a real difference in
              people's lives every single day.
            </motion.p>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2
          }} className="mt-10" data-id="element-286">
              <button className="bg-brand-red text-white px-8 py-4 rounded-full font-bold text-lg hover:opacity-90 transition-all shadow-lg shadow-red-900/20 inline-flex items-center gap-3" data-id="element-287">
                View Open Positions <ArrowRight size={20} data-id="element-288" />
              </button>
            </motion.div>
          </div>
        </div>

        {/* Why Work With Us */}
        <section className="py-20" data-id="element-289">
          <div className="container mx-auto px-4 md:px-6" data-id="element-290">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="text-center mb-16" data-id="element-291">
              <h2 className="text-3xl md:text-4xl font-extrabold text-brand-blue mb-6" data-id="element-292">
                Why Drive With Us?
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto" data-id="element-293">
                We believe in taking care of our team so they can take the best
                possible care of our patients.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-294">
              {[{
              icon: DollarSign,
              title: 'Competitive Pay',
              desc: 'Top-tier hourly rates, overtime opportunities, and performance bonuses.'
            }, {
              icon: Briefcase,
              title: 'Great Benefits',
              desc: 'Comprehensive health, dental, and vision insurance for full-time employees.'
            }, {
              icon: GraduationCap,
              title: 'Paid Training',
              desc: 'Fully paid CPR, First Aid, and specialized transport certification training.'
            }, {
              icon: Users,
              title: 'Supportive Culture',
              desc: 'A family-like environment where management knows your name and values your input.'
            }].map((benefit, i) => <motion.div key={i} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: i * 0.1,
              duration: 0.5
            }} className="bg-gray-50 p-8 rounded-3xl text-center hover:bg-brand-light transition-colors group" data-id="element-295">
                  <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center text-brand-blue mx-auto mb-6 shadow-sm group-hover:scale-110 transition-transform" data-id="element-296">
                    <benefit.icon size={32} data-id="element-297" />
                  </div>
                  <h3 className="text-xl font-bold text-brand-blue mb-4" data-id="element-298">
                    {benefit.title}
                  </h3>
                  <p className="text-gray-600" data-id="element-299">{benefit.desc}</p>
                </motion.div>)}
            </div>
          </div>
        </section>

        {/* Requirements */}
        <section className="py-20 bg-brand-blue text-white" data-id="element-300">
          <div className="container mx-auto px-4 md:px-6" data-id="element-301">
            <div className="grid lg:grid-cols-2 gap-16 items-center" data-id="element-302">
              <motion.div initial={{
              opacity: 0,
              x: -20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6
            }} data-id="element-303">
                <h2 className="text-3xl md:text-4xl font-extrabold mb-6" data-id="element-304">
                  Driver Requirements
                </h2>
                <p className="text-blue-100 text-lg mb-8 leading-relaxed" data-id="element-305">
                  We maintain the highest standards for our team to ensure the
                  safety and comfort of our patients. To join MediRide USA,
                  applicants must meet the following criteria:
                </p>
                <ul className="space-y-4" data-id="element-306">
                  {["Valid state driver's license with a clean driving record", 'Minimum 21 years of age', 'Ability to pass a comprehensive background check and drug screening', 'Physical ability to assist patients and operate transport equipment', 'Strong communication skills and a compassionate demeanor'].map((req, i) => <motion.li key={i} initial={{
                  opacity: 0,
                  x: -10
                }} whileInView={{
                  opacity: 1,
                  x: 0
                }} viewport={{
                  once: true
                }} transition={{
                  delay: i * 0.1
                }} className="flex items-start gap-3 text-blue-50" data-id="element-307">
                      <div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center shrink-0 mt-0.5" data-id="element-308">
                        <span className="text-sm font-bold" data-id="element-309">{i + 1}</span>
                      </div>
                      <span data-id="element-310">{req}</span>
                    </motion.li>)}
                </ul>
              </motion.div>
              <motion.div initial={{
              opacity: 0,
              scale: 0.95
            }} whileInView={{
              opacity: 1,
              scale: 1
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.2
            }} className="bg-white text-brand-blue p-8 md:p-12 rounded-3xl shadow-2xl" data-id="element-311">
                <h3 className="text-2xl font-bold mb-6" data-id="element-312">Ready to apply?</h3>
                <p className="text-gray-600 mb-8" data-id="element-313">
                  Fill out our quick initial application and a member of our
                  recruiting team will be in touch within 48 hours.
                </p>
                <form className="space-y-4" data-id="element-314">
                  <input type="text" placeholder="Full Name" className="w-full p-4 rounded-xl border border-gray-200 focus:border-brand-blue focus:ring-2 focus:ring-brand-blue outline-none transition-all" data-id="element-315" />
                  <input type="email" placeholder="Email Address" className="w-full p-4 rounded-xl border border-gray-200 focus:border-brand-blue focus:ring-2 focus:ring-brand-blue outline-none transition-all" data-id="element-316" />
                  <input type="tel" placeholder="Phone Number" className="w-full p-4 rounded-xl border border-gray-200 focus:border-brand-blue focus:ring-2 focus:ring-brand-blue outline-none transition-all" data-id="element-317" />
                  <button type="button" className="w-full bg-brand-red text-white py-4 rounded-xl font-bold text-lg hover:opacity-90 transition-colors" data-id="element-318">
                    Submit Application
                  </button>
                </form>
              </motion.div>
            </div>
          </div>
        </section>
      </div>
    </PageTransition>;
}